/**
 * The chat singleton.
 *
 * IWSDK instantiates systems itself, so a module-level runtime is the simplest
 * way for a system and the panel-wiring code to share one conversation without
 * threading it through `registerSystem` config.
 *
 * Created lazily by `initChat` so the API base URL — which is resolved from the
 * page URL at boot — can be injected rather than read from a global.
 */
import { BackendGateway } from './BackendGateway.js';
import { ConversationService } from './ConversationService.js';
import { PlaybackService } from './PlaybackService.js';
import { Store } from '../core/store.js';

/** Shown before any conversation exists, and whenever the guide offers none. */
export const FALLBACK_SUGGESTIONS: readonly string[] = [
  'What can you help me with?',
  'Tell me something interesting.',
  'What should I ask you?',
];

const GREETING = "Hello — I'm your guide. Ask me anything, or pick one of these to start.";

export interface ChatRuntime {
  conversation: ConversationService;
  playback: PlaybackService;
  /** Cold-start prompts, replaced by the backend's when it offers some. */
  suggestions: Store<readonly string[]>;
  /** Seed the opening line. Safe to call more than once; only the first counts. */
  greet(): void;
  ask(text: string): void;
  /** Choose the nth follow-up (1-based) offered by the last reply. */
  chooseFollowUp(ordinal: number): void;
}

let runtime: ChatRuntime | undefined;

export function initChat(apiBaseUrl: string): ChatRuntime {
  const gateway = new BackendGateway(apiBaseUrl);
  const conversation = new ConversationService(gateway);
  const playback = new PlaybackService();
  const suggestions = new Store<readonly string[]>(FALLBACK_SUGGESTIONS);

  // Any new guide message starts revealing. Watching message identity rather
  // than count means a message whose text is still streaming keeps the same
  // reveal rather than restarting it on every token.
  let revealingId: string | null = null;
  conversation.subscribe((state) => {
    const last = state.messages[state.messages.length - 1];
    if (!last || last.role !== 'guide') return;
    if (last.id === revealingId) return;
    revealingId = last.id;
    playback.begin(last.id, last.text);
  });

  // Keep the reveal fed as tokens extend a message already in flight.
  conversation.subscribe((state) => {
    const last = state.messages[state.messages.length - 1];
    if (!last || last.role !== 'guide' || last.id !== revealingId) return;
    playback.extend(last.text);
  });

  let greeted = false;
  runtime = {
    conversation,
    playback,
    suggestions,
    greet() {
      if (greeted) return;
      greeted = true;
      conversation.seedGreeting(GREETING, { followUps: [...suggestions.getSnapshot()] });
    },
    ask(text) {
      void conversation.ask(text).then(() => {
        const offered = conversation.currentFollowUps();
        if (offered.length > 0) suggestions.set(offered);
      });
    },
    chooseFollowUp(ordinal) {
      const offered = conversation.currentFollowUps();
      const choice = offered[ordinal - 1];
      if (choice) runtime?.ask(choice);
    },
  };

  void fetchSuggestions(apiBaseUrl).then((fetched) => {
    if (fetched.length > 0) suggestions.set(fetched);
  });

  return runtime;
}

/** Access the runtime. Throws rather than silently no-op if used before init. */
export const chat: ChatRuntime = new Proxy({} as ChatRuntime, {
  get(_target, property) {
    if (!runtime) throw new Error('Chat runtime used before initChat()');
    return Reflect.get(runtime, property) as unknown;
  },
});

/**
 * Cold-start prompts from the backend. Entirely optional — a failure leaves the
 * built-in fallbacks in place, so the guide always has something to offer.
 */
async function fetchSuggestions(apiBaseUrl: string): Promise<string[]> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    const response = await fetch(`${apiBaseUrl}/suggested-questions`, {
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!response.ok) return [];
    const body: unknown = await response.json();
    const questions = (body as { questions?: unknown })?.questions;
    if (!Array.isArray(questions)) return [];
    return questions.filter((q): q is string => typeof q === 'string').slice(0, 3);
  } catch {
    return [];
  }
}
